/**
 * Reusable popover-based select components extracted from InboxFilterBar.
 *
 * MultiSelectPopover — checkbox list with optional search + "clear" footer
 * SingleSelectPopover — radio-style single select
 * FilterChip — active filter pill with × remove button
 */
import { useState } from 'react';
import { Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

/**
 * MultiSelectPopover — Popover with checkbox list, optional search, clear footer.
 */
export function MultiSelectPopover({
  trigger,
  options,
  selected = [],
  onChange,
  searchable,
  emptyText = 'Nenhuma opção',
  align = 'start',
  className,
}) {
  const [search, setSearch] = useState('');
  const filtered = searchable
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className={cn('w-56 p-0 bg-surface border-border-strong', className)} align={align}>
        {searchable && (
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground-muted" />
              <Input
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-7 pl-7 text-xs bg-surface-active border-border-strong text-foreground-strong placeholder:text-placeholder"
              />
            </div>
          </div>
        )}
        <div className="max-h-48 overflow-y-auto py-1 custom-scrollbar">
          {filtered.length === 0 && (
            <p className="text-xs text-foreground-muted px-3 py-2">{emptyText}</p>
          )}
          {filtered.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-hover cursor-pointer text-sm text-foreground-secondary"
            >
              <Checkbox
                checked={selected.includes(opt.value)}
                onCheckedChange={(checked) => {
                  onChange(
                    checked
                      ? [...selected, opt.value]
                      : selected.filter((v) => v !== opt.value),
                  );
                }}
                className="h-3.5 w-3.5 border-border-strong data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
              />
              {opt.dot && <span className={cn('h-2 w-2 rounded-full shrink-0', opt.dot)} />}
              <span className="truncate">{opt.label}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <>
            <Separator className="bg-surface-active" />
            <button
              onClick={() => onChange([])}
              className="w-full text-xs text-foreground-muted hover:text-foreground-secondary py-1.5 transition-colors"
            >
              Limpar seleção
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * SingleSelectPopover — Popover with radio-style single select.
 */
export function SingleSelectPopover({ trigger, options, selected, onChange, allowClear = true, align = 'start', className }) {
  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className={cn('w-52 p-0 bg-surface border-border-strong', className)} align={align}>
        <div className="max-h-48 overflow-y-auto py-1 custom-scrollbar">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onChange(selected === opt.value && allowClear ? null : opt.value)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors text-left',
                selected === opt.value
                  ? 'bg-surface-active text-foreground-strong'
                  : 'text-foreground-muted hover:bg-surface-hover hover:text-foreground',
              )}
            >
              {opt.dot && <span className={cn('h-2 w-2 rounded-full shrink-0', opt.dot)} />}
              <span className="truncate">{opt.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * FilterChip — Active filter pill with × remove button.
 */
export function FilterChip({ label, color, onRemove }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors',
        color || 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border-cyan-500/30 dark:border-cyan-500/20',
      )}
    >
      {label}
      <button onClick={onRemove} className="hover:text-foreground-strong transition-colors ml-0.5">
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}
